"use client";
import { RiSortDesc } from "@remixicon/react";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
} from "@workspace/ui/components/select";
import { useCallback } from "react";
import { cn } from "@/lib/utils";

export interface SortOption<T = string> {
	value: T;
	label: string;
	icon?: React.ReactNode;
}

export interface UniversalSortDropdownProps<T = string> {
	value: T;
	onValueChange: (value: T) => void;
	options: SortOption<T>[];
	placeholder?: string;
	className?: string;
	showIcon?: boolean;
	preserveScrollPosition?: boolean;
}

function UniversalSortDropdownComponent<T extends string = string>({
	value,
	onValueChange,
	options,
	placeholder = "Sort by...",
	className,
	showIcon = true,
	preserveScrollPosition = false,
}: UniversalSortDropdownProps<T>) {
	const handleValueChange = useCallback(
		(newValue: string) => {
			if (preserveScrollPosition) {
				const scrollPosition = window.scrollY;
				onValueChange(newValue as T);
				requestAnimationFrame(() => {
					window.scrollTo(0, scrollPosition);
				});
			} else {
				onValueChange(newValue as T);
			}
		},
		[onValueChange, preserveScrollPosition],
	);

	const selectedOption = options.find((opt) => opt.value === value);
	const displayLabel = selectedOption?.label || placeholder;

	return (
		<Select value={value as string} onValueChange={handleValueChange}>
			<SelectTrigger
				className={cn(
					"hover:bg-accent hover:text-accent-foreground active:scale-[0.99]",
					className,
				)}
			>
				<div className="flex items-center gap-2">
					{showIcon && <RiSortDesc className="h-4 w-4 shrink-0" />}
					<span className="truncate">{displayLabel}</span>
				</div>
			</SelectTrigger>
			<SelectContent>
				{options.map((option) => (
					<SelectItem key={option.value} value={option.value as string}>
						<div className="flex items-center gap-2">
							{option.icon}
							<span>{option.label}</span>
						</div>
					</SelectItem>
				))}
			</SelectContent>
		</Select>
	);
}

export const UniversalSortDropdown = UniversalSortDropdownComponent;

// Pre-configured sort options for common use cases
export const FESTIVAL_SORT_OPTIONS: SortOption[] = [
	{ value: "time-asc", label: "Earliest First" },
	{ value: "time-desc", label: "Latest First" },
	{ value: "artist-asc", label: "A to Z" },
	{ value: "artist-desc", label: "Z to A" },
	{ value: "rating-desc", label: "Rated Highest" },
	{ value: "rating-asc", label: "Rated Lowest" },
];

export const ARTIST_SORT_OPTIONS: SortOption[] = [
	{ value: "popularity", label: "By Popularity" },
	{ value: "alpha", label: "A to Z" },
	{ value: "alpha-desc", label: "Z to A" },
];

export const DATE_SORT_OPTIONS: SortOption[] = [
	{ value: "date-desc", label: "Newest First" },
	{ value: "date-asc", label: "Oldest First" },
];

export const GENERIC_SORT_OPTIONS: SortOption[] = [
	{ value: "name-asc", label: "A to Z" },
	{ value: "name-desc", label: "Z to A" },
	{ value: "date-desc", label: "Newest First" },
	{ value: "date-asc", label: "Oldest First" },
];
